import React from "react";
import { Box } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { C, FONT_BODY, VISUALLY_HIDDEN } from "../../../utils/designTokens";
import { CardTitle, DetailCard, GroupHeading } from "./detailUi";

const DOT = 28;
const STATE_WORDS = { done: "done", current: "in progress", future: "not yet", cancelled: "" };

function Dot({ state, fill }) {
  const base = { position: "relative", zIndex: 1, boxSizing: "border-box", width: DOT, height: DOT, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" };
  if (state === "done") {
    return (
      <Box sx={{ ...base, bgcolor: fill, color: "#FFFFFF" }}>
        <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
      </Box>
    );
  }
  if (state === "cancelled") {
    return (
      <Box sx={{ ...base, bgcolor: C.red, color: "#FFFFFF" }}>
        <CloseRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
      </Box>
    );
  }
  if (state === "current") {
    return (
      <Box sx={{ ...base, bgcolor: C.bg, border: `2px solid ${C.red}`, boxShadow: `0 0 0 5px ${C.redTint}` }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: C.red,
            "@keyframes odPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
            animation: "odPulse 1.6s ease-in-out infinite",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      </Box>
    );
  }
  return <Box sx={{ ...base, bgcolor: C.bg, border: `2px solid ${C.borderStrong}` }} />;
}

/**
 * One vertical timeline: the restaurant's steps, then the delivery's (or the
 * pickup's). Done steps are filled (red while live, green once delivered),
 * the step under way is ringed and is the page's aria-current step.
 */
function Timeline({ timeline, phase }) {
  const fill = phase === "complete" ? C.green : phase === "cancelled" ? C.muted : C.red;
  const reached = (st) => st && (st.state === "done" || st.state === "current" || st.state === "cancelled");

  return (
    <DetailCard aria-labelledby="od-timeline-title">
      <CardTitle id="od-timeline-title">Timeline</CardTitle>
      <Box component="ol" sx={{ m: 0, mt: 2, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2.5 }}>
        {timeline.groups.map((g) => (
          <Box component="li" key={g.key}>
            <GroupHeading sx={{ mb: 1.25 }}>{g.title}</GroupHeading>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {g.steps.map((st, i) => {
                const next = g.steps[i + 1];
                const isCurrent = st.key === timeline.currentKey;
                const strong = st.state === "current" || st.state === "cancelled";
                return (
                  <Box
                    component="li"
                    key={st.key}
                    aria-current={isCurrent ? "step" : undefined}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.75,
                      pb: next ? 2 : 0,
                      // The connector to the next dot: from under this dot to the top of the next.
                      ...(next
                        ? {
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: DOT / 2 - 1,
                              top: DOT,
                              bottom: 0,
                              width: 2,
                              bgcolor: reached(next) && next.state !== "cancelled" ? fill : C.borderStrong,
                            },
                          }
                        : {}),
                    }}
                  >
                    <Dot state={st.state} fill={fill} />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: DOT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        fontFamily: FONT_BODY,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: 15,
                          fontWeight: strong ? 700 : 500,
                          color: st.state === "cancelled" ? C.red : st.state === "future" ? C.muted : C.ink,
                          lineHeight: 1.3,
                        }}
                      >
                        {st.label}
                        {STATE_WORDS[st.state] && <Box component="span" sx={VISUALLY_HIDDEN}>{`, ${STATE_WORDS[st.state]}`}</Box>}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          fontSize: 14,
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          color: st.state === "cancelled" ? C.red : C.muted,
                        }}
                      >
                        {st.state === "future" || !st.time ? "—" : st.time}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </DetailCard>
  );
}

export default Timeline;
